import { useCallback } from 'react';
import { useOptionalDependencies } from '@servicetitan/react-ioc';
import {
    PrequalsApi,
    PreApprovalActivity,
} from '@servicetitan/financing-api-client/src/prequals-api';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { isRenderableBannerPreApproval } from '@servicetitan/financing-utils-shared';
import { PreApprovalStore } from './preApprovalStore';

/**
 * Returns a fire-and-forget logger that records the customer's response to a pre-approval offer via the
 * user-activity endpoint (`POST /v1/preapprovals/{id}/user-activity`).
 *
 * No-ops when the pre-approval id or tenant id is missing, and swallows ALL errors: activity logging is
 * best-effort and must never block or break the flow. Callers gate on a rendered/renderable offer.
 */
export const useLogPreApprovalActivity = (
    preApprovalId: string | null | undefined,
    tenantId: string | null | undefined,
) => {
    // Optional resolution: the plans Container that calls this also renders in apps that don't
    // register PrequalsApi (e.g. application-management's edit-plan flow via loan-tasks-shared).
    // When it's absent the logger safely no-ops instead of throwing on render.
    const [apiClient] = useOptionalDependencies(PrequalsApi);

    return useCallback(
        (activity: PreApprovalActivity): void => {
            if (!apiClient || !preApprovalId || !tenantId) {
                return;
            }

            apiClient
                .preApprovalsLogUserActivity(preApprovalId, { tenantId, activity })
                .catch(() => {
                    // Best-effort logging; ignore failures.
                });
        },
        [apiClient, preApprovalId, tenantId],
    );
};

/**
 * Binds `useLogPreApprovalActivity` to the pre-approval and tenant in context and reports whether a
 * Synchrony banner pre-approval is currently renderable, so callers outside the banner (plan selection,
 * application close) can log the same offer's activity under the same gate.
 */
export const useSynchronyPreApprovalActivity = () => {
    const { preApproval } = PreApprovalStore.useStore();
    const { setupData } = SetupDataStore.useStore();

    return {
        log: useLogPreApprovalActivity(preApproval?.id, setupData?.tenantId),
        isRenderable: isRenderableBannerPreApproval(setupData?.provider, preApproval),
    };
};
